import React, { useState, useEffect } from 'react'

import {
  Backdrop,
  ModalStyle,
  IconWrapper,
  ImageWrapper,
  TextWrapper,
  Header,
  SocialAppWrapper,
  LinkWrapper
} from './ShareModal.style'
import { useQueryClient } from '@tanstack/react-query'

import {
  close,
  CopyLinkIcon,
  MoreHorizontal,
  WhatsAppIcon,
  InstagramIcon,
  FacebookIcon,
  TwitterIcon,
  MessengerIcon,
  MailIcon
} from 'shared-ui/assets'
import { media } from 'shared-ui/utils/media.js'

import { useToast, ShimmerEffect } from 'shared-ui/components'
import { useResponsive, useNativeShare } from 'shared-ui/hooks'
import { SUPER_APP_CONFIG } from 'shared-ui/utils/constants'
import { useShareCount } from 'shared-ui/hooks'

function ShareModal({ openShareModal, onClose, shareData }) {
  const { showSuccess, showError } = useToast()
  const isMobile = useResponsive(media.mobile)
  const [loading, setLoading] = useState(true)
  const share = useNativeShare()
  const queryClient = useQueryClient()
  const { mutate: shareCount, isPending } = useShareCount()
  const handleShare = (contentId, isUGC, queryKey) => {
    shareCount(
      {
        content_id: contentId,
        isUGC
      },
      {
        onSuccess: (response) => {
          if(queryKey){
          queryClient.invalidateQueries({
            queryKey: [queryKey]
          })
        }
         shareData?.onShareCountSuccess?.(response)
        }
      }
    )
  }
  useEffect(() => {
    if (shareData?.url) {
      setLoading(false)
    }
  }, [shareData])

  const handleCopyURL = async () => {
    try {
      await navigator.clipboard.writeText(shareData?.url)
      showSuccess(SUPER_APP_CONFIG.SHARE_COPIED_SUCCESS)
      handleShare(shareData?.id, shareData?.isUGC, shareData?.key)
    } catch {
      showError(SUPER_APP_CONFIG.SHARE_COPIED_FAIL)
    }
  }

  const handleMoreOptions = async () => {
    handleShare(shareData?.id, shareData?.isUGC, shareData?.key)
    share({
      title: shareData?.title,
      text: shareData?.text,
      url: shareData?.url
    })
    onClose()
  }

  const SHARE_APPS = [
    //whatsapp
    {
      name: 'WhatsApp',
      domain: 'whatsapp.com',
      image: WhatsAppIcon,
      getLink: () => {
        const message =
          `${shareData?.text || ''} ${shareData?.url || ''}`.trim()

        const encodedMessage = encodeURIComponent(message)
        if (isMobile) {
          // Try opening WhatsApp app for mweb
          return `https://api.whatsapp.com/send?text=${encodedMessage}`
        }

        // Desktop fallback for whats app web
        return `https://web.whatsapp.com/send?text=${encodedMessage}`
      }
    },
    // {
    //   name: 'Instagram',
    //   domain: 'instagram.com',
    //   image: InstagramIcon,
    //   getLink: ({ url, text }) =>
    //     `https://www.instagram.com/accounts/login/?next=/share/web/`
    // },
    //facebook sharing
    {
      name: 'Facebook',
      domain: 'facebook.com',
      image: FacebookIcon,
      getLink: () =>
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareData?.url
        )}`
    },
    //twitter sharing
    {
      name: 'Twitter',
      domain: 'twitter.com',
      image: TwitterIcon,
      getLink: () =>
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(
          shareData?.url
        )}&text=${encodeURIComponent(shareData?.text)}`
    },
    // {
    //   name: 'Messenger',
    //   domain: 'messenger.com',
    //   image: MessengerIcon,
    //   getLink: () => {
    //     const appId = '1656717342266666';

    //     return (
    //       `https://www.facebook.com/dialog/send?` +
    //       `app_id=${appId}` +
    //       `&link=${encodeURIComponent(shareData?.url)}` +
    //       `&redirect_uri=${encodeURIComponent(window.location.origin)}`
    //     );
    //   }
    // },
    {
      name: 'Email',
      domain: 'mailto',
      image: MailIcon,
      getLink: () =>
        `mailto:?subject=${encodeURIComponent(
          shareData?.text
        )}&body=${encodeURIComponent(
          `${shareData?.text}\n\n${shareData?.url}`
        )}`
    }
  ]

  if (!openShareModal) return null

  // content render
  const RenderShareOptions = () => {
    return (
      <>
        <div className="flex items-center justify-between mb-6">
          <Header>{SUPER_APP_CONFIG.CREATE_CONTENT_DETAILS_ALT_SHARE}</Header>
          <img
            src={close}
            alt="Copy Link"
            onClick={onClose}
            style={{ cursor: 'pointer' }}
          />
        </div>
        {loading ? (
          <SocialAppWrapper isMobile={isMobile}>
            {Array.from({ length: 7 }).map((_, index) => (
              <IconWrapper key={index}>
                <ShimmerEffect width="56px" height="56px" radius="50%" />
                <ShimmerEffect
                  width="56px"
                  height="12px"
                  radius="4px"
                  className="mt-1"
                />
              </IconWrapper>
            ))}
          </SocialAppWrapper>
        ) : (
          <SocialAppWrapper isMobile={isMobile}>
            {/* CopyLink Button  */}
            <IconWrapper>
              <LinkWrapper onClick={handleCopyURL}>
                <img src={CopyLinkIcon} alt="Copy Link" />
              </LinkWrapper>
              <TextWrapper>Copy Link</TextWrapper>
            </IconWrapper>

            {/* Social Apps */}
            {SHARE_APPS.map((app) => (
              <IconWrapper key={app.name}>
                <ImageWrapper
                  src={app.image}
                  alt={app.name}
                  onClick={() => {
                    handleShare(shareData?.id, shareData?.isUGC, shareData?.key)
                    window.open(app.getLink(), '_blank')
                    onClose()
                  }}
                />
                <TextWrapper>{app.name}</TextWrapper>
              </IconWrapper>
            ))}

            {/* More Button */}
            <IconWrapper>
              <LinkWrapper onClick={handleMoreOptions}>
                <img src={MoreHorizontal} alt="More Options" />
              </LinkWrapper>
              <TextWrapper>More</TextWrapper>
            </IconWrapper>
          </SocialAppWrapper>
        )}
      </>
    )
  }

  return (
    <Backdrop isMobile={isMobile}>
      <ModalStyle isMobile={isMobile}>
        <RenderShareOptions />
      </ModalStyle>
    </Backdrop>
  )
}

export default ShareModal
